import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { roleHasPermission, type Permission, type TenantRole } from '@endcopilot/shared';
import { api, type ApiClient } from './api';
import type { Me, TenantSummary } from './types';

type Status = 'loading' | 'anonymous' | 'authenticated';

interface SessionValue {
  status: Status;
  me: Me | null;
  tenants: TenantSummary[];
  tenant: TenantSummary | null;
  /** Papel efetivo na empresa selecionada. */
  role: TenantRole | 'SUPERADMIN' | null;
  selectTenant: (id: string) => void;
  can: (p: Permission) => boolean;
  /** Recebe o access token emitido pelo login/MFA e carrega o perfil. */
  completeLogin: (accessToken: string) => Promise<void>;
  reload: () => Promise<void>;
  reloadTenants: () => Promise<void>;
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);
const TENANT_KEY = 'endcopilot.tenant';

function readStoredTenant(): string | null {
  try {
    return localStorage.getItem(TENANT_KEY);
  } catch {
    return null;
  }
}

function storeTenant(id: string | null): void {
  try {
    if (id) localStorage.setItem(TENANT_KEY, id);
    else localStorage.removeItem(TENANT_KEY);
  } catch {
    /* armazenamento indisponível: só perde a preferência */
  }
}

export function SessionProvider({
  children,
  client = api,
}: {
  children: ReactNode;
  client?: ApiClient;
}) {
  const [status, setStatus] = useState<Status>('loading');
  const [me, setMe] = useState<Me | null>(null);
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [tenantId, setTenantId] = useState<string | null>(null);

  const loadTenants = useCallback(
    async (profile: Me) => {
      const list = profile.user.isSuperadmin
        ? await client.get<TenantSummary[]>('/v1/admin/tenants')
        : profile.tenants;
      setTenants(list);
      const stored = readStoredTenant();
      const chosen = list.find((t) => t.id === stored) ?? (list.length === 1 ? list[0] : undefined);
      client.tenantId = chosen?.id ?? null;
      setTenantId(chosen?.id ?? null);
    },
    [client],
  );

  const load = useCallback(async () => {
    const profile = await client.get<Me>('/v1/me');
    setMe(profile);
    // Com senha temporária a API só libera /v1/me: a lista de empresas vem depois da troca.
    if (!profile.user.mustChangePassword) await loadTenants(profile);
    setStatus('authenticated');
  }, [client, loadTenants]);

  const reset = useCallback(() => {
    client.accessToken = null;
    client.tenantId = null;
    setMe(null);
    setTenants([]);
    setTenantId(null);
    setStatus('anonymous');
  }, [client]);

  useEffect(() => {
    client.onSessionLost = reset;
    let alive = true;
    // Ao abrir o painel, tenta restaurar a sessão pelo cookie de refresh.
    void client.refresh().then(async (ok) => {
      if (!alive) return;
      if (!ok) return reset();
      try {
        await load();
      } catch {
        reset();
      }
    });
    return () => {
      alive = false;
      client.onSessionLost = null;
    };
  }, [client, load, reset]);

  const value = useMemo<SessionValue>(() => {
    const tenant = tenants.find((t) => t.id === tenantId) ?? null;
    const role: SessionValue['role'] = me?.user.isSuperadmin
      ? 'SUPERADMIN'
      : (tenant?.role ?? null);
    return {
      status,
      me,
      tenants,
      tenant,
      role,
      selectTenant: (id) => {
        client.tenantId = id;
        storeTenant(id);
        setTenantId(id);
      },
      can: (p) => role === 'SUPERADMIN' || (role !== null && roleHasPermission(role, p)),
      completeLogin: async (accessToken) => {
        client.accessToken = accessToken;
        await load();
      },
      reload: load,
      reloadTenants: async () => {
        if (me) await loadTenants(me);
      },
      logout: async () => {
        await client.post('/v1/auth/logout', {}, { anonymous: true }).catch(() => undefined);
        reset();
      },
    };
  }, [status, me, tenants, tenantId, client, load, loadTenants, reset]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(SessionContext);
  if (!v) throw new Error('useSession fora de SessionProvider');
  return v;
}
